import { useEffect, useMemo, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";

const getTodayLocalISODate = () => {
  const now = new Date();
  const localMidnight = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  return localMidnight.toISOString().split("T")[0];
};

const getMinimumStartDate = () => {
  // October 27, 2025 is the earliest allowed start date
  const minimumDate = new Date(2025, 9, 27); // Month is 0-indexed, so 9 = October
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  // Return whichever is later: October 27, 2025 or today
  return minimumDate > today ? minimumDate.toISOString().split("T")[0] : getTodayLocalISODate();
};

const formatLocalDate = (value: string) => {
  const [year, month, day] = value.split("-").map(Number);
  if (!year || !month || !day) return value;
  return new Date(year, month - 1, day).toLocaleDateString();
};

export default function Confirm() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { toast } = useToast();
  const [loading, setLoading] = useState(false);
  const minStartDate = useMemo(() => getMinimumStartDate(), []);
  const [startDate, setStartDate] = useState(minStartDate);
  const [confirmed, setConfirmed] = useState(false);
  const token = searchParams.get("token");

  useEffect(() => {
    const error = searchParams.get("error");
    if (error) {
      let errorMessage = "An error occurred.";
      if (error === "invalid_token") {
        errorMessage = "This confirmation link is invalid.";
      } else if (error === "expired_token") {
        errorMessage = "This confirmation link has expired.";
      } else if (error === "server_error") {
        errorMessage = "A server error occurred. Please try again.";
      }
      
      toast({
        title: "Error",
        description: errorMessage,
        variant: "destructive",
      });
      navigate("/");
    } else if (!token) {
      toast({
        title: "Invalid link",
        description: "This confirmation link is invalid.",
        variant: "destructive",
      });
      navigate("/");
    }
  }, [token, searchParams, navigate, toast]);

  const handleConfirm = async () => {
    if (!token) return;

    setLoading(true);
    try {
      const { data, error } = await supabase.functions.invoke("confirm", {
        body: { token, start_date: startDate },
      });

      if (error) {
        throw new Error(error.message || "Failed to confirm subscription");
      }

      if (data?.error) {
        throw new Error(data.error);
      }

      setConfirmed(true);
    } catch (error: any) {
      toast({
        title: "Error",
        description: error.message || "Failed to confirm. Please try again.",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  if (confirmed) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-background via-background to-secondary/20 flex items-center justify-center">
        <div className="bg-card border border-border rounded-lg shadow-lg p-8 max-w-md w-full text-center">
          <div className="text-success text-5xl mb-4">✓</div>
          <h1 className="text-2xl font-bold mb-2">You're All Set!</h1>
          <p className="text-muted-foreground mb-6">
            Your 21-day AI journey starts on {formatLocalDate(startDate)}.
            Watch your inbox for daily lessons!
          </p>
          <Button onClick={() => navigate("/")} className="w-full">
            Back to Home
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-background via-background to-secondary/20 flex items-center justify-center">
      <div className="bg-card border border-border rounded-lg shadow-lg p-8 max-w-md w-full">
        <h1 className="text-2xl font-bold mb-2">Confirm Your Subscription</h1>
        <p className="text-muted-foreground mb-6">
          Choose when you'd like to start receiving your daily emails
        </p>

        <div className="space-y-4">
          <div>
            <Label htmlFor="startDate">Start Date</Label>
            <Input
              id="startDate"
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              min={minStartDate}
            />
          </div>

          <Button
            onClick={handleConfirm}
            disabled={loading}
            className="w-full"
          >
            {loading ? "Confirming..." : "Confirm Subscription"}
          </Button>
        </div>
      </div>
    </div>
  );
}
